// #region Imports
import { useState, useEffect, useRef } from 'react';
import { useDispatch as useReduxDispatch, useSelector } from 'react-redux';
import type { ThunkDispatch, UnknownAction } from '@reduxjs/toolkit';
import { useForm } from 'react-hook-form';
import {
  selectIsFeedbackOpen,
  selectIsFeedbackExpired,
  sendFeedback,
  setFeedBackOpen,
  setLastFeedbackDate,
} from './AZFeedbackSlice';
import type { FeedbackData } from './AZFeedbackSlice';
import type { AZFeedbackProps } from './AZFeedback.types';
import { AttachmentError, prepareAttachmentsForStorage } from '../../utils/fileUtils';
// #endregion

// #region Helpers

/**
 * Normalises an unknown `catch` value into a human-readable error string.
 *
 * Handles two shapes that can reach a `catch` block here:
 * - A native `Error` instance (e.g. a rejected promise from `onSendFeedback`).
 * - A plain object with a `message: string` property — the serialised
 *   `action.error` that Redux Toolkit's `unwrap()` re-throws when the thunk
 *   rejects (RTK serialises the original Error to `{ message, name, stack? }`).
 *
 * Returns `undefined` when neither shape is recognised so the caller can supply
 * a sensible fallback.
 */
const getErrorMessage = (err: unknown): string | undefined => {
  if (err instanceof Error) return err.message;
  if (
    err !== null &&
    typeof err === 'object' &&
    'message' in err &&
    typeof (err as Record<string, unknown>)['message'] === 'string'
  ) {
    return (err as Record<string, unknown>)['message'] as string;
  }
  return undefined;
};

// #endregion

// #region Hook
interface UseAZFeedbackReturn {
  isFeedbackOpen: boolean | 'manually';
  formChoice: 'feedback' | 'bug' | null;
  isLoading: boolean;
  methods: ReturnType<typeof useForm>;
  setFormChoice: (choice: 'feedback' | 'bug' | null) => void;
  handleSendFeedback: (data: Record<string, unknown>) => Promise<void>;
  handleValidateStep: () => Promise<boolean>;
  handleGoBack: () => void;
  handleClose: () => void;
}

const useAZFeedback = ({
  appName,
  appVersion,
  username,
  userEmail,
  onSendFeedback,
  onExpired,
  attachmentOptions,
}: AZFeedbackProps): UseAZFeedbackReturn => {
  // #region State
  const [isLoading, setIsLoading] = useState(false);
  const [formChoice, setFormChoice] = useState<'feedback' | 'bug' | null>(null);
  // #endregion

  // #region Selectors
  const isFeedbackOpen = useSelector(selectIsFeedbackOpen);
  const isFeedbackExpired = useSelector(selectIsFeedbackExpired);
  // #endregion

  const dispatch = useReduxDispatch<ThunkDispatch<unknown, unknown, UnknownAction>>();

  const methods = useForm();
  const { reset, trigger, setError } = methods;

  // #region Effects
  const onExpiredRef = useRef(onExpired);
  onExpiredRef.current = onExpired;

  useEffect(() => {
    if (!isFeedbackExpired || !onExpiredRef.current) return;
    const cleanup = onExpiredRef.current(() => dispatch(setFeedBackOpen('manually')));
    return typeof cleanup === 'function' ? cleanup : undefined;
  }, [isFeedbackExpired, dispatch]);
  // #endregion

  // #region Events
  const handleSendFeedback = async (data: Record<string, unknown>) => {
    // Clear any previous submission error before a new attempt.
    methods.clearErrors('root.submit');

    setIsLoading(true);

    // Encode raw File attachments to base64 before they enter the (serializable)
    // Redux payload. Surface validation failures on the form and abort on error.
    const { attachments: rawAttachments, ...rest } = data;
    let attachments: FeedbackData['attachments'];
    try {
      const files = Array.isArray(rawAttachments) ? (rawAttachments as File[]) : [];
      if (files.length > 0) {
        attachments = await prepareAttachmentsForStorage(files, attachmentOptions);
      }
    } catch (err) {
      const message =
        err instanceof AttachmentError
          ? err.errors.map((e) => e.message).join(' ')
          : 'Failed to process attachments.';
      setError('attachments', { type: 'manual', message });
      setIsLoading(false);
      return;
    }

    const payload: FeedbackData = {
      ...(appName !== undefined && { appName }),
      ...(appVersion !== undefined && { appVersion }),
      userAgent: navigator.userAgent,
      ...(userEmail !== undefined && { email: userEmail }),
      ...(username !== undefined && { username }),
      date: new Date().toISOString(),
      type: formChoice,
      ...(rest as Partial<FeedbackData>),
      ...(attachments && attachments.length > 0 && { attachments }),
    };

    try {
      await dispatch(sendFeedback({ data: payload, apiFn: onSendFeedback })).unwrap();
      dispatch(setLastFeedbackDate(new Date().toISOString()));
      // Success: close and reset the form.
      handleClose();
    } catch (err) {
      // Failure: keep the modal open and preserve the user's input.
      // Surface a submit-level error so the UI can display it near the Submit button.
      setError('root.submit', {
        type: 'manual',
        message: getErrorMessage(err) ?? 'Failed to send. Please try again.',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleValidateStep = async () => {
    try {
      return await trigger();
    } catch {
      return false;
    }
  };

  const handleGoBack = () => {
    reset();
    setFormChoice(null);
  };

  const handleClose = () => {
    reset();
    setFormChoice(null);
    dispatch(setFeedBackOpen(false));
  };
  // #endregion

  return {
    isFeedbackOpen,
    formChoice,
    isLoading,
    methods,
    setFormChoice,
    handleSendFeedback,
    handleValidateStep,
    handleGoBack,
    handleClose,
  };
};
// #endregion

export { useAZFeedback };
